/**
 * @file `<Objective/>` — interactive objective card (#629, #1114).
 * @module @labcraft/course-kit/components/objective
 *
 * Author-facing API (resolved from the chapter spec via `useObjective`):
 *
 *   <Objective id="cowsay">
 *     Optional extra prose under the goal.
 *   </Objective>
 *
 * The card renders the objective's `goal` (+ optional `constraint`) and
 * its status (completed / active / rethink / upcoming). There is **no
 * Run button** — per the #927 lab-UX rethink, "the typing is the
 * doing": the learner types commands in the terminal and OSC 133
 * detection advances the objective. The only exception is a `manual`
 * objective (one whose `satisfiedBy` includes `{ via: 'manual' }`),
 * which shows an "I did it" button that dispatches completion directly.
 *
 * The command surface splits by `kind` (umbrella #1114):
 *
 *   - **demonstration** — the canonical `commands` sequence is the
 *     teaching surface, so once the beat is reached (active / rethink /
 *     done) the card shows every command as its own copyable block with
 *     its `explain` alongside. Multi-command beats number the blocks
 *     (`Command 2 of 3`); commands that matched get a check; `optional`
 *     commands are tagged and never gate advancement.
 *   - **challenge** — the commands are withheld behind a **reveal
 *     ladder**: the learner discloses one `hint` rung at a time, and the
 *     final rung reveals the full command sequence + explanations.
 *     Revealing is **free** — it is pure local card state, never
 *     dispatches, never blocks advancement, and is not styled or
 *     recorded as a failure. A solved (done) challenge shows the
 *     sequence as reference.
 *
 * In either kind, an `automatic` command is a completion check the lab
 * runs itself (a `state` probe), so it renders as a "Checked
 * automatically" row with its explanation instead of a copyable command,
 * and the `Command N of M` numbering counts only the typed commands.
 *
 * When the active objective is in `rethink` (a wrong turn fired), the
 * card surfaces the mentor's `mentorHook` — advisory only; it never
 * blocks the learner from completing the objective.
 */

import {
  Children,
  isValidElement,
  useState,
  type JSX,
  type ReactNode,
} from 'react'
import {
  useChapterDispatch,
  useObjective,
  type ObjectiveStatus,
} from '../runtime/context.tsx'
import type { ObjectiveCommand } from '../types.ts'
import { Aside } from './aside.tsx'
import { renderInline } from './glossary.tsx'
import { CheckIcon, CopyIcon, InfoCircleIcon } from './icons.tsx'
import styles from './objective.module.css'

export interface ObjectiveProps {
  /** Stable id matching the chapter spec's `objectives[].id`. */
  id: string
  /** Optional extra instruction prose shown under the goal. */
  children?: ReactNode
}

/**
 * Status word shown next to the marker + rail. Build Spec §5: state is
 * signalled by marker + word + rail color, **never color alone** — so
 * the word is load-bearing, not decorative.
 */
const STATUS_LABEL: Record<ObjectiveStatus, string> = {
  locked: 'Locked',
  active: 'Active',
  rethink: 'Rethink',
  done: 'Done',
}

/**
 * One canonical command as a copyable code surface plus its token-level
 * explanation. Shared by demonstration cards (the teaching surface) and
 * a challenge's final reveal rung / solved state.
 *
 * @param props.command - the canonical command to show + copy
 * @param props.explain - the token-level explanation rendered beneath it,
 *   authored as inline markdown (code spans, emphasis)
 * @param props.label - the uppercase caption above the command
 * @param props.optional - render the "optional" (non-gating) tag
 * @param props.done - render the run check on the label row
 * @returns the command block element
 */
function CommandBlock(props: {
  command: string
  explain: string
  label: string
  optional?: boolean
  done?: boolean
}): JSX.Element {
  const [copied, setCopied] = useState(false)

  const onCopy = (): void => {
    if (typeof navigator === 'undefined' || !navigator.clipboard) return
    void navigator.clipboard.writeText(props.command).then(
      () => setCopied(true),
      () => {},
    )
  }

  return (
    <div className={styles.commandBlock}>
      <span className={styles.commandLabel}>
        {props.label}
        {props.optional && (
          <span className={styles.commandOptionalTag}>optional</span>
        )}
        {props.done && (
          <span className={styles.commandCheck} aria-label="Run">
            <CheckIcon />
          </span>
        )}
      </span>
      <div className={styles.commandRow}>
        <code className={styles.command}>{props.command}</code>
        <button
          type="button"
          className={styles.copyBtn}
          onClick={onCopy}
          title="Copy command">
          <CopyIcon />
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      {props.explain.trim() !== '' && (
        <p className={styles.explain}>{renderInline(props.explain)}</p>
      )}
    </div>
  )
}

/**
 * A platform-run completion check in the sequence (`automatic` command):
 * the state the lab verifies on its own once the learner's commands have
 * run. It shows no command text and no copy button, because there is
 * nothing to type; printing the probe as a copyable block reads as one
 * more step.
 *
 * @param props.explain - what the check confirms, as inline markdown
 * @param props.done - render the pass check on the label row
 * @returns the check block element
 */
function AutomaticCheck(props: {
  explain: string
  done: boolean
}): JSX.Element {
  return (
    <div className={`${styles.commandBlock} ${styles.automaticCheck}`}>
      <span className={styles.commandLabel}>
        Checked automatically
        {props.done && (
          <span className={styles.commandCheck} aria-label="Passed">
            <CheckIcon />
          </span>
        )}
      </span>
      {props.explain.trim() !== '' && (
        <p className={styles.explain}>{renderInline(props.explain)}</p>
      )}
    </div>
  )
}

/**
 * The objective's full command sequence as a run of {@link CommandBlock}s.
 * Single-command beats keep the bare label; multi-command beats number
 * each block (`Command 2 of 3`) so the card reads as a sequence. Automatic
 * checks render as {@link AutomaticCheck} and are left out of the
 * numbering, which counts only the commands the learner types.
 *
 * @param props.commands - the objective's ordered canonical commands
 * @param props.satisfied - indices that matched the event stream
 * @param props.label - the single-command caption ("Command" /
 *   "The command")
 * @returns the sequence element
 */
function CommandSequence(props: {
  commands: ObjectiveCommand[]
  satisfied: ReadonlySet<number>
  label: string
}): JSX.Element {
  const typed = props.commands.filter(c => c.automatic !== true).length
  let position = 0
  return (
    <>
      {props.commands.map((c, i) => {
        if (c.automatic === true)
          return (
            <AutomaticCheck
              key={i}
              explain={c.explain}
              done={props.satisfied.has(i)}
            />
          )

        position += 1
        return (
          <CommandBlock
            key={i}
            command={c.command}
            explain={c.explain}
            label={
              typed === 1 ? props.label : `Command ${position} of ${typed}`
            }
            optional={c.optional === true}
            done={props.satisfied.has(i)}
          />
        )
      })}
    </>
  )
}

/**
 * A challenge's reveal ladder. The learner discloses one hint rung at a
 * time; the final rung reveals the command sequence + explanations.
 * State is local to the card and is **never dispatched** — revealing is
 * free and has no bearing on completion (Build Spec locked decision 1).
 * `hints` is non-empty on a challenge (builder-enforced by
 * `lintChapter`).
 *
 * @param props.hints - ordered hint rungs before the command sequence,
 *   each authored as inline markdown
 * @param props.commands - the canonical sequence (the implicit final rung)
 * @param props.satisfied - indices that matched the event stream
 * @returns the reveal-ladder element
 */
function RevealLadder(props: {
  hints: string[]
  commands: ObjectiveCommand[]
  satisfied: ReadonlySet<number>
}): JSX.Element {
  // 0 → nothing shown; 1..hints.length → that many hints; > hints.length
  // → the command rung is revealed too.
  const [revealed, setRevealed] = useState(0)
  const commandRevealed = revealed > props.hints.length
  const nextIsCommand = revealed === props.hints.length
  const typed = props.commands.filter(c => c.automatic !== true).length

  return (
    <div className={styles.ladder}>
      <span className={styles.ladderCaption}>
        Work it out from the goal. Reveal a rung if you’re stuck — it’s free and
        never counts against you.
      </span>

      {revealed > 0 && (
        <ol className={styles.hintList}>
          {props.hints.slice(0, revealed).map((hint, i) => (
            <li key={i} className={styles.hint}>
              {renderInline(hint)}
            </li>
          ))}
        </ol>
      )}

      {commandRevealed && (
        <CommandSequence
          commands={props.commands}
          satisfied={props.satisfied}
          label="The command"
        />
      )}

      {!commandRevealed && (
        <button
          type="button"
          className={styles.revealBtn}
          onClick={() => setRevealed(r => r + 1)}>
          {nextIsCommand
            ? typed === 1
              ? 'Reveal the command'
              : 'Reveal the commands'
            : `Reveal a hint (${revealed + 1}/${props.hints.length})`}
        </button>
      )}
    </div>
  )
}

export function Objective(props: ObjectiveProps): JSX.Element | null {
  const view = useObjective(props.id)
  const dispatch = useChapterDispatch()

  if (!view) {
    if (typeof console !== 'undefined') {
      console.warn(
        `<Objective id="${props.id}"> not found in chapter spec; ignoring.`,
      )
    }
    return null
  }

  const { status, objective } = view
  const isDone = status === 'done'
  const isLocked = status === 'locked'
  const isActive = status === 'active'
  const isRethink = status === 'rethink'
  const isManual = objective.commands.some(c =>
    c.satisfiedBy.some(d => d.via === 'manual'),
  )
  const isChallenge = objective.kind === 'challenge'

  // An `<Aside>` authored inside the objective annotates this beat's
  // command, so it renders *inside* the card after the command surface —
  // and only once the beat is reached, so a caution about a command never
  // shows before the command itself. Everything else (the lead-in prose)
  // stays above the command sequence.
  const children = Children.toArray(props.children)
  const asides = children.filter(c => isValidElement(c) && c.type === Aside)
  const leadIn = children.filter(c => !(isValidElement(c) && c.type === Aside))

  const onMarkDone = (): void => {
    if (isLocked || isDone) return
    dispatch({ type: 'objective.completed', id: objective.id })
  }

  const stepClass = [
    styles.step,
    isActive ? styles.stepActive : '',
    isRethink ? styles.stepRethink : '',
    isDone ? styles.stepDone : '',
    isLocked ? styles.stepLocked : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={stepClass}>
      <span className={styles.rail} aria-hidden />
      <div className={styles.head}>
        <span className={styles.marker} aria-label={STATUS_LABEL[status]}>
          {isDone && <CheckIcon />}
          {isActive && <span className={styles.pulse} />}
          {isRethink && <InfoCircleIcon />}
          {isLocked && <span className={styles.dot} />}
        </span>
        <span className={styles.meta}>
          <span className={styles.label}>Objective {view.positionLabel}</span>
          <span className={styles.status}>{STATUS_LABEL[status]}</span>
        </span>
      </div>

      {/* The goal is the card's heading; constraint is the lesson-encoding
          phrasing ("without installing it"). The body prose (children)
          renders beneath it as a distinct block. */}
      <p className={styles.goal}>
        <span dangerouslySetInnerHTML={{ __html: objective.goal }} />
        {objective.constraint && (
          <em className={styles.constraint}>
            {' — '}
            {objective.constraint}
          </em>
        )}
      </p>
      {leadIn.length > 0 && <div className={styles.instruction}>{leadIn}</div>}

      {/* Command surface. A demonstration shows the command sequence
          outright once the beat is reached; a challenge withholds it
          behind the reveal ladder while active, and shows it as
          reference once solved. The sequence stays hidden on a locked
          (not-yet-reached) card. */}
      {!isLocked &&
        (isChallenge ? (
          isDone ? (
            <CommandSequence
              commands={objective.commands}
              satisfied={view.satisfiedCommands}
              label="The command"
            />
          ) : (
            <RevealLadder
              hints={objective.hints}
              commands={objective.commands}
              satisfied={view.satisfiedCommands}
            />
          )
        ) : (
          <CommandSequence
            commands={objective.commands}
            satisfied={view.satisfiedCommands}
            label="Command"
          />
        ))}

      {/* Command-scoped asides authored inside the objective: the curl
          security caution, the classic-CLI note. Rendered here so they
          sit under the command they annotate, and only once the beat is
          reached. */}
      {!isLocked && asides.length > 0 && (
        <div className={styles.cardAsides}>{asides}</div>
      )}

      {/* "The typing is the doing" (#927). The active card never
          pre-fills or runs the command — it cues the learner to type it
          themselves in the terminal. Suppressed for `manual` objectives,
          which carry their own "I did it" affordance below. */}
      {isActive && !isManual && (
        <p className={styles.typeCue}>
          Type it yourself in the terminal — that’s the lab.
        </p>
      )}

      {/* Rethink: a first-class state, not a red error. The mentor's
          message lives in the dock (#939) — the card just flags the state
          and points there, so the hook has a single home. Advisory only;
          a satisfying command still advances the objective. */}
      {isRethink && (
        <p className={styles.mentorFlag}>
          <InfoCircleIcon />
          The mentor flagged how you got here — see the dock.
        </p>
      )}

      {isManual && (
        <div className={styles.manualBar}>
          <button
            className={styles.manual}
            type="button"
            onClick={onMarkDone}
            disabled={isLocked || isDone}
            title={
              isLocked
                ? 'Complete the previous objective first'
                : isDone
                  ? 'Already marked done'
                  : 'Mark this objective complete'
            }>
            <CheckIcon />
            {isDone ? 'Done' : 'I did it'}
          </button>
        </div>
      )}
    </div>
  )
}
